import { createBrowserRouter, RouterProvider, Outlet, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { AppHeader } from '../components/layout';
import { TourProvider } from '../components/onboarding/TourProvider';
import { TourOverlay } from '../components/onboarding/TourOverlay';
import { libraryOnboardingTour } from '../components/onboarding/tours/library-onboarding';
import { editOnboardingTour } from '../components/onboarding/tours/edit-onboarding';
import { BranchLivePresenter } from '../components/notification/BranchLivePresenter';

// Lazy load pages for code splitting
const LandingPage = lazy(() => import('../pages/LandingPage'));
const Library = lazy(() => import('../pages/Library'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const BranchWorkspace = lazy(() => import('../pages/BranchWorkspace'));
const PublishConfirm = lazy(() => import('../pages/PublishConfirm'));
const AuthCallback = lazy(() => import('../pages/AuthCallback'));
const AuditLog = lazy(() => import('../pages/AuditLog'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const SignupPage = lazy(() => import('../pages/SignupPage'));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
// Org/Team admin lives inside `/profile?tab=admin`. Only the team-detail
// deep-link keeps a standalone route (linked from the Sub-teams list).
const TeamDetail = lazy(() => import('../pages/admin/TeamDetail'));

// Spec 022 — asset library (browse) + inline gallery view (preview +
// download). The slug-param route maps to the same AssetLibrary
// component so the sidebar / toolbar don't re-mount when an asset is
// selected; AssetLibrary reads useParams() to decide between grid
// mode and gallery mode in its main body.
const AssetLibrary = lazy(() =>
  import('../pages/AssetLibrary').then((m) => ({ default: m.AssetLibrary }))
);

// Loading fallback
function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <p className="mt-2 text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

// Auth pages where the header should be hidden
const AUTH_ROUTES = ['/login', '/signup', '/verify-email', '/forgot-password', '/reset-password', '/auth/callback'];

// Reset window scroll to (0, 0) on pathname changes so navigating to a
// fresh page never preserves the previous page's scroll offset (e.g.,
// homepage → /library should land at the top, not the footer). Skips
// when the URL has a hash so anchor links still scroll to their target.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Root layout component
function RootLayout() {
  const { pathname } = useLocation();
  const hideHeader = AUTH_ROUTES.includes(pathname);

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <TourProvider tours={[libraryOnboardingTour, editOnboardingTour]}>
        <div className="min-h-screen bg-white">
          {!hideHeader && <AppHeader />}
          <Suspense fallback={<LoadingFallback />}>
            <Outlet />
          </Suspense>
          <TourOverlay />
          {!hideHeader && <BranchLivePresenter />}
        </div>
      </TourProvider>
    </ErrorBoundary>
  );
}

// Router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: 'library',
        element: <Library />,
      },
      // Feature 016: team detail deep-link (org/members/teams list live in /profile?tab=admin)
      { path: 'admin/teams/:id', element: <TeamDetail /> },
      // Spec 022 — asset library. Must come BEFORE 'library/:slug' so the
      // specific 'library/assets' path doesn't get captured by the slug
      // wildcard. A SINGLE route with an optional :slug param keeps the
      // AssetLibrary instance mounted across grid ↔ gallery transitions
      // (two sibling routes pointing at the same element still get
      // reconciled as distinct element instances and cause a full
      // remount — which replays the toolbar's slideUpIn animation).
      // The slug param toggles the main body between grid mode and
      // gallery mode without unmounting the sidebar + bottom toolbar.
      {
        path: 'library/assets/:slug?',
        element: <AssetLibrary />,
      },
      {
        path: 'library/:slug',
        element: <Library />,
      },
      // Unified profile page
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      // Redirects from old routes to unified profile
      {
        path: 'dashboard',
        element: <Navigate to="/library" replace />,
      },
      {
        path: 'notifications',
        element: <Navigate to="/profile?tab=notifications" replace />,
      },
      {
        path: 'settings/notifications',
        element: <Navigate to="/profile?tab=settings" replace />,
      },
      {
        path: 'settings/account',
        element: <Navigate to="/profile?tab=settings" replace />,
      },
      // Email/password auth routes (010-email-password-auth)
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'signup',
        element: <SignupPage />,
      },
      {
        path: 'verify-email',
        element: <VerifyEmailPage />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      // Auth callback route (Phase 3 - US1)
      {
        path: 'auth/callback',
        element: <AuthCallback />,
      },
      // Branch routes (Phase 3 - US1)
      {
        path: 'branches/:id',
        element: <BranchWorkspace />,
      },
      // Content edit route (Phase 5 - Inline Edit)
      {
        path: 'branches/:id/content/:contentId/edit',
        element: <BranchWorkspace />,
      },
      // Publish routes (Phase 5 - US3)
      {
        path: 'branches/:id/publish',
        element: <PublishConfirm />,
      },
      // Audit log routes (Phase 8 - US6)
      {
        path: 'audit',
        element: <AuditLog />,
      },
      // Redirect old admin route to unified profile
      {
        path: 'admin',
        element: <Navigate to="/profile?tab=admin" replace />,
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;
